// ============================================
// SCOUT PROGRAM CONSTANTS
// ============================================
// Point values for each action type
export const SCOUT_POINT_VALUES = {
    SCAN: 10,
    FEEDBACK: 15,
    ISSUE_REPORT: 25,
    STATUS_REPORT: 20,
    PHOTO: 10,
    FIRST_SCAN_OF_DAY: 15,
    STREAK_BONUS_PER_DAY: 5,
    STREAK_BONUS_CAP: 50,
    REFERRAL_BONUS: 100,
    COMPANY_REFERRAL_BONUS: 500,
};
// Ranger rank thresholds (Globe rebrand)
export const SCOUT_LEVEL_THRESHOLDS = [
    { level: 1, xp: 0, name: 'Field Scout' },
    { level: 2, xp: 500, name: 'Trail Scout' },
    { level: 3, xp: 1250, name: 'Tracker' },
    { level: 4, xp: 2250, name: 'Senior Tracker' },
    { level: 5, xp: 3750, name: 'Ranger' },
    { level: 6, xp: 5750, name: 'Senior Ranger' },
    { level: 7, xp: 8750, name: 'Lead Ranger' },
    { level: 8, xp: 13250, name: 'Chief Ranger' },
    { level: 9, xp: 19250, name: 'Master Ranger' },
    { level: 10, xp: 27250, name: 'Legend' },
];
// Daily earning caps (max number of times per day)
export const SCOUT_DAILY_CAPS = {
    SCAN: 20,
    FEEDBACK: 10,
    ISSUE_REPORT: 5,
    STATUS_REPORT: 10,
    PHOTO: 5,
};
// Scout acquisition sources
export const SCOUT_SOURCES = ['ORGANIC', 'OPERATOR', 'EMPLOYEE'];
// Scout point transaction types
export const SCOUT_TRANSACTION_TYPES = [
    'SCAN',
    'FEEDBACK',
    'ISSUE_REPORT',
    'STATUS_REPORT',
    'PHOTO',
    'FIRST_SCAN_OF_DAY',
    'STREAK_BONUS',
    'MISSION_REWARD',
    'BADGE_REWARD',
    'REWARD_REDEMPTION',
    'REFERRAL_BONUS',
    'COMPANY_REFERRAL_BONUS',
];
/**
 * Get the level for a given total XP amount.
 */
export function getScoutLevel(totalXp) {
    for (let i = SCOUT_LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
        const threshold = SCOUT_LEVEL_THRESHOLDS[i];
        if (totalXp >= threshold.xp) {
            return threshold;
        }
    }
    return SCOUT_LEVEL_THRESHOLDS[0];
}
/**
 * Get XP needed for next level. Returns null if at max level.
 */
export function getXpForNextLevel(totalXp) {
    const currentLevel = getScoutLevel(totalXp);
    const nextThreshold = SCOUT_LEVEL_THRESHOLDS.find((t) => t.level === currentLevel.level + 1);
    return nextThreshold ? nextThreshold.xp - totalXp : null;
}
/**
 * Get point value for an action type.
 * Accepts optional context for future dynamic XP support.
 */
export function getPointValue(actionType, _context) {
    return SCOUT_POINT_VALUES[actionType] ?? 0;
}
// ============================================
// PHASE 2: MISSIONS
// ============================================
export const MISSION_TYPES = ['DAILY', 'WEEKLY', 'SPECIAL'];
export const MISSION_STATUSES = ['ACTIVE', 'COMPLETED', 'CLAIMED', 'EXPIRED'];
export const MISSION_CRITERIA_TYPES = [
    'SCAN',
    'FEEDBACK',
    'ISSUE_REPORT',
    'PHOTO',
    'UNIQUE_BUILDINGS',
    'STREAK',
];
/** Number of daily missions assigned per Scout per day */
export const DAILY_MISSIONS_COUNT = 3;
/** Mission template definitions for seeding */
export const MISSION_TEMPLATE_DEFINITIONS = [
    {
        slug: 'daily-scanner',
        name: 'Daily Scanner',
        description: 'Scan 3 amenities today',
        type: 'DAILY',
        criteriaType: 'SCAN',
        target: 3,
        xpReward: 50,
        minLevel: 1,
        weight: 3,
    },
    {
        slug: 'feedback-champion',
        name: 'Feedback Champion',
        description: 'Leave 2 feedback submissions today',
        type: 'DAILY',
        criteriaType: 'FEEDBACK',
        target: 2,
        xpReward: 40,
        minLevel: 1,
        weight: 3,
    },
    {
        slug: 'issue-spotter-mission',
        name: 'Issue Spotter',
        description: 'Report 1 issue today',
        type: 'DAILY',
        criteriaType: 'ISSUE_REPORT',
        target: 1,
        xpReward: 30,
        minLevel: 1,
        weight: 2,
    },
    {
        slug: 'photo-reporter',
        name: 'Photo Reporter',
        description: 'Submit 1 photo today',
        type: 'DAILY',
        criteriaType: 'PHOTO',
        target: 1,
        xpReward: 25,
        minLevel: 2,
        weight: 1,
    },
    {
        slug: 'building-explorer-mission',
        name: 'Building Explorer',
        description: 'Scan at 3 different buildings this week',
        type: 'WEEKLY',
        criteriaType: 'UNIQUE_BUILDINGS',
        target: 3,
        xpReward: 100,
        minLevel: 2,
        weight: 2,
    },
    {
        slug: 'streak-builder',
        name: 'Streak Builder',
        description: 'Maintain your streak all week',
        type: 'WEEKLY',
        criteriaType: 'STREAK',
        target: 7,
        xpReward: 150,
        minLevel: 3,
        weight: 1,
    },
    {
        slug: 'scan-marathon',
        name: 'Scan Marathon',
        description: 'Complete 15 scans this week',
        type: 'WEEKLY',
        criteriaType: 'SCAN',
        target: 15,
        xpReward: 120,
        minLevel: 2,
        weight: 2,
    },
    {
        slug: 'level-up-mission',
        name: 'Level Up',
        description: 'Reach the next level',
        type: 'SPECIAL',
        criteriaType: 'SCAN',
        target: 1,
        xpReward: 200,
        minLevel: 1,
        weight: 1,
    },
];
// ============================================
// PHASE 2: BADGES
// ============================================
export const BADGE_RARITIES = ['COMMON', 'UNCOMMON', 'RARE', 'EPIC', 'LEGENDARY'];
export const BADGE_CATEGORIES = ['FUNDAMENTALS', 'STREAKS', 'EXPLORATION', 'QUALITY', 'SOCIAL', 'SPECIAL'];
export const BADGE_CRITERIA_TYPES = [
    'TOTAL_SCANS',
    'TOTAL_FEEDBACK',
    'STREAK_DAYS',
    'UNIQUE_BUILDINGS',
    'TOTAL_ISSUES',
    'TOTAL_PHOTOS',
    'REFERRALS',
    'COMPANY_REFERRALS',
    'UNIQUE_TENANTS',
    'SPECIAL',
];
/** Badge definitions for seeding — 23 badges across 6 categories */
export const BADGE_DEFINITIONS = [
    // Fundamentals
    {
        slug: 'first-scan',
        name: 'First Scan',
        description: 'Complete your first scan',
        rarity: 'COMMON',
        category: 'FUNDAMENTALS',
        criteriaType: 'TOTAL_SCANS',
        criteriaThreshold: 1,
        xpReward: 10,
        isSecret: false,
        displayOrder: 1,
    },
    {
        slug: 'feedback-starter',
        name: 'Feedback Starter',
        description: 'Submit 5 feedback reports',
        rarity: 'COMMON',
        category: 'FUNDAMENTALS',
        criteriaType: 'TOTAL_FEEDBACK',
        criteriaThreshold: 5,
        xpReward: 25,
        isSecret: false,
        displayOrder: 2,
    },
    {
        slug: 'regular',
        name: 'Regular',
        description: 'Complete 25 scans',
        rarity: 'UNCOMMON',
        category: 'FUNDAMENTALS',
        criteriaType: 'TOTAL_SCANS',
        criteriaThreshold: 25,
        xpReward: 50,
        isSecret: false,
        displayOrder: 3,
    },
    {
        slug: 'dedicated',
        name: 'Dedicated',
        description: 'Complete 100 scans',
        rarity: 'RARE',
        category: 'FUNDAMENTALS',
        criteriaType: 'TOTAL_SCANS',
        criteriaThreshold: 100,
        xpReward: 100,
        isSecret: false,
        displayOrder: 4,
    },
    {
        slug: 'veteran',
        name: 'Veteran',
        description: 'Complete 500 scans',
        rarity: 'EPIC',
        category: 'FUNDAMENTALS',
        criteriaType: 'TOTAL_SCANS',
        criteriaThreshold: 500,
        xpReward: 250,
        isSecret: false,
        displayOrder: 5,
    },
    // Streaks
    {
        slug: '3-day-streak',
        name: '3-Day Streak',
        description: 'Maintain a 3-day streak',
        rarity: 'COMMON',
        category: 'STREAKS',
        criteriaType: 'STREAK_DAYS',
        criteriaThreshold: 3,
        xpReward: 15,
        isSecret: false,
        displayOrder: 10,
    },
    {
        slug: 'week-warrior',
        name: 'Week Warrior',
        description: 'Maintain a 7-day streak',
        rarity: 'UNCOMMON',
        category: 'STREAKS',
        criteriaType: 'STREAK_DAYS',
        criteriaThreshold: 7,
        xpReward: 50,
        isSecret: false,
        displayOrder: 11,
    },
    {
        slug: 'fortnight-force',
        name: 'Fortnight Force',
        description: 'Maintain a 14-day streak',
        rarity: 'RARE',
        category: 'STREAKS',
        criteriaType: 'STREAK_DAYS',
        criteriaThreshold: 14,
        xpReward: 100,
        isSecret: false,
        displayOrder: 12,
    },
    {
        slug: 'month-master',
        name: 'Month Master',
        description: 'Maintain a 30-day streak',
        rarity: 'EPIC',
        category: 'STREAKS',
        criteriaType: 'STREAK_DAYS',
        criteriaThreshold: 30,
        xpReward: 250,
        isSecret: false,
        displayOrder: 13,
    },
    {
        slug: 'century',
        name: 'Century',
        description: 'Maintain a 100-day streak',
        rarity: 'LEGENDARY',
        category: 'STREAKS',
        criteriaType: 'STREAK_DAYS',
        criteriaThreshold: 100,
        xpReward: 1000,
        isSecret: false,
        displayOrder: 14,
    },
    // Exploration
    {
        slug: 'building-explorer',
        name: 'Building Explorer',
        description: 'Scan at 3 different buildings',
        rarity: 'UNCOMMON',
        category: 'EXPLORATION',
        criteriaType: 'UNIQUE_BUILDINGS',
        criteriaThreshold: 3,
        xpReward: 50,
        isSecret: false,
        displayOrder: 20,
    },
    {
        slug: 'city-scout',
        name: 'City Ranger',
        description: 'Scan at 10 different buildings',
        rarity: 'RARE',
        category: 'EXPLORATION',
        criteriaType: 'UNIQUE_BUILDINGS',
        criteriaThreshold: 10,
        xpReward: 150,
        isSecret: false,
        displayOrder: 21,
    },
    {
        slug: 'globe-trotter',
        name: 'Globe Trotter',
        description: 'Scan at 25 different buildings',
        rarity: 'EPIC',
        category: 'EXPLORATION',
        criteriaType: 'UNIQUE_BUILDINGS',
        criteriaThreshold: 25,
        xpReward: 500,
        isSecret: false,
        displayOrder: 22,
    },
    {
        slug: 'logo-starter',
        name: 'Logo Starter',
        description: 'Collect logos from 3 different companies',
        rarity: 'UNCOMMON',
        category: 'EXPLORATION',
        criteriaType: 'UNIQUE_TENANTS',
        criteriaThreshold: 3,
        xpReward: 75,
        isSecret: false,
        displayOrder: 23,
    },
    {
        slug: 'brand-collector',
        name: 'Brand Collector',
        description: 'Collect logos from 10 different companies',
        rarity: 'RARE',
        category: 'EXPLORATION',
        criteriaType: 'UNIQUE_TENANTS',
        criteriaThreshold: 10,
        xpReward: 200,
        isSecret: false,
        displayOrder: 24,
    },
    {
        slug: 'logo-legend',
        name: 'Logo Legend',
        description: 'Collect logos from 25 different companies',
        rarity: 'EPIC',
        category: 'EXPLORATION',
        criteriaType: 'UNIQUE_TENANTS',
        criteriaThreshold: 25,
        xpReward: 500,
        isSecret: false,
        displayOrder: 25,
    },
    // Quality
    {
        slug: 'issue-spotter',
        name: 'Issue Spotter',
        description: 'Report 10 issues',
        rarity: 'UNCOMMON',
        category: 'QUALITY',
        criteriaType: 'TOTAL_ISSUES',
        criteriaThreshold: 10,
        xpReward: 50,
        isSecret: false,
        displayOrder: 30,
    },
    {
        slug: 'quality-eye',
        name: 'Quality Eye',
        description: 'Report 50 issues',
        rarity: 'RARE',
        category: 'QUALITY',
        criteriaType: 'TOTAL_ISSUES',
        criteriaThreshold: 50,
        xpReward: 150,
        isSecret: false,
        displayOrder: 31,
    },
    {
        slug: 'photographer',
        name: 'Photographer',
        description: 'Submit 10 photos',
        rarity: 'UNCOMMON',
        category: 'QUALITY',
        criteriaType: 'TOTAL_PHOTOS',
        criteriaThreshold: 10,
        xpReward: 50,
        isSecret: false,
        displayOrder: 32,
    },
    // Social
    {
        slug: 'recruiter',
        name: 'Recruiter',
        description: 'Refer 1 Ranger',
        rarity: 'UNCOMMON',
        category: 'SOCIAL',
        criteriaType: 'REFERRALS',
        criteriaThreshold: 1,
        xpReward: 50,
        isSecret: false,
        displayOrder: 40,
    },
    {
        slug: 'ambassador',
        name: 'Ambassador',
        description: 'Refer 5 Rangers',
        rarity: 'RARE',
        category: 'SOCIAL',
        criteriaType: 'REFERRALS',
        criteriaThreshold: 5,
        xpReward: 150,
        isSecret: false,
        displayOrder: 41,
    },
    {
        slug: 'company-connector',
        name: 'Company Connector',
        description: 'Refer 1 company signup',
        rarity: 'EPIC',
        category: 'SOCIAL',
        criteriaType: 'COMPANY_REFERRALS',
        criteriaThreshold: 1,
        xpReward: 500,
        isSecret: false,
        displayOrder: 42,
    },
    // Special
    {
        slug: 'early-adopter',
        name: 'Early Adopter',
        description: 'Sign up in the first month',
        rarity: 'LEGENDARY',
        category: 'SPECIAL',
        criteriaType: 'SPECIAL',
        criteriaThreshold: 1,
        xpReward: 500,
        isSecret: true,
        displayOrder: 50,
    },
];
// ============================================
// PHASE 2: LEADERBOARD
// ============================================
export const LEADERBOARD_PERIODS = ['DAILY', 'WEEKLY', 'MONTHLY', 'ALL_TIME'];
/** Cache duration in minutes */
export const LEADERBOARD_CACHE_MINUTES = 15;
/** Max entries per leaderboard */
export const LEADERBOARD_MAX_ENTRIES = 100;
// ============================================
// PHASE 3: REWARDS
// ============================================
export const REWARD_TYPES = ['VIRTUAL', 'PLATFORM'];
export const REWARD_CATEGORIES = ['MERCH', 'DIGITAL', 'FEATURE', 'EXPERIENCE'];
export const REDEMPTION_STATUSES = ['PENDING', 'FULFILLED', 'CANCELLED'];
/** Reward definitions for seeding */
export const REWARD_DEFINITIONS = [
    // Merch
    {
        slug: 'sticker-pack',
        name: 'TendedLoop Sticker Pack',
        description: 'A set of 5 TendedLoop Globe stickers',
        type: 'PLATFORM',
        category: 'MERCH',
        pointCost: 500,
        stockLimit: 100,
        minLevel: 2,
        displayOrder: 1,
    },
    {
        slug: 'scout-badge-pin',
        name: 'Ranger Badge Pin',
        description: 'Limited edition enamel Ranger pin',
        type: 'PLATFORM',
        category: 'MERCH',
        pointCost: 1500,
        stockLimit: 50,
        minLevel: 4,
        displayOrder: 2,
    },
    {
        slug: 'scout-t-shirt',
        name: 'Ranger T-Shirt',
        description: 'Exclusive TendedLoop Globe t-shirt',
        type: 'PLATFORM',
        category: 'MERCH',
        pointCost: 5000,
        stockLimit: 25,
        minLevel: 6,
        displayOrder: 3,
    },
    // Digital
    {
        slug: 'custom-avatar-frame',
        name: 'Custom Avatar Frame',
        description: 'Unlock a special frame for your Ranger profile',
        type: 'VIRTUAL',
        category: 'DIGITAL',
        pointCost: 300,
        stockLimit: null,
        minLevel: 2,
        displayOrder: 10,
    },
    {
        slug: 'profile-theme',
        name: 'Profile Theme',
        description: 'Unlock a premium profile color theme',
        type: 'VIRTUAL',
        category: 'DIGITAL',
        pointCost: 750,
        stockLimit: null,
        minLevel: 3,
        displayOrder: 11,
    },
    {
        slug: 'animated-badge',
        name: 'Animated Badge Effect',
        description: 'Add sparkle animations to your earned badges',
        type: 'VIRTUAL',
        category: 'DIGITAL',
        pointCost: 1000,
        stockLimit: null,
        minLevel: 5,
        displayOrder: 12,
    },
    // Feature Access
    {
        slug: 'detailed-analytics',
        name: 'Personal Analytics',
        description: 'Unlock detailed personal scan analytics and trends',
        type: 'VIRTUAL',
        category: 'FEATURE',
        pointCost: 2000,
        stockLimit: null,
        minLevel: 4,
        displayOrder: 20,
    },
    {
        slug: 'priority-missions',
        name: 'Priority Missions',
        description: 'Get access to exclusive high-reward missions',
        type: 'VIRTUAL',
        category: 'FEATURE',
        pointCost: 3000,
        stockLimit: null,
        minLevel: 5,
        displayOrder: 21,
    },
    // Experience
    {
        slug: 'facility-tour',
        name: 'Behind-the-Scenes Tour',
        description: 'Get a guided tour of facility operations',
        type: 'PLATFORM',
        category: 'EXPERIENCE',
        pointCost: 4000,
        stockLimit: 10,
        minLevel: 7,
        displayOrder: 30,
    },
    {
        slug: 'scout-spotlight',
        name: 'Ranger Spotlight',
        description: 'Featured on the Ranger community page for a week',
        type: 'PLATFORM',
        category: 'EXPERIENCE',
        pointCost: 2500,
        stockLimit: null,
        minLevel: 5,
        displayOrder: 31,
    },
];
// ============================================
// PHASE 4: REFERRALS
// ============================================
export const REFERRAL_TYPES = ['SCOUT', 'COMPANY'];
export const REFERRAL_STATUSES = ['PENDING', 'CONVERTED', 'EXPIRED'];
/** Daily limits for referral invites */
export const REFERRAL_DAILY_LIMITS = { SCOUT: 10, COMPANY: 5 };
// Scout PWA URL (for links from other apps)
export const SCOUT_PWA_URL_DEV = 'http://localhost:3005';
export const SCOUT_PWA_URL_PROD = 'https://tendedloop-scout.pages.dev';
//# sourceMappingURL=scout-constants.js.map